import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    LabelList,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis
} from 'recharts';
import {
    Award,
    BarChart3,
    Check,
    Copy,
    FileSearch,
    FileWarning,
    FileSignature,
    Lightbulb,
    MessageCircle,
    Minus,
    Snowflake,
    TrendingDown,
    TrendingUp,
    UserPlus,
    Users,
    Wallet,
    XCircle,
    Zap
} from 'lucide-react';
import { supabase } from '../../../src/lib/supabase';
import { getFunctionErrorMessage } from '../../../src/lib/functionErrors';
import { api } from '../../../src/services/api';
import { useAuth } from '../../../src/contexts/AuthContext';
import { Profile, TarifaEnergia } from '../../../types';
import ResponsibleFilter from '../../../src/components/ResponsibleFilter';
import LoadingSpinner from '../../../src/components/LoadingSpinner';
import { fmtKwh, fmtNum, fmtPct, isEffectivelyAtOrAfter, STATUS_LABELS } from '../../../src/lib/funnelUtils';
import { computeEventFunnel } from './eventFunnel';
import type { EventFunnelRow } from './eventFunnel';
import { aplicarContagemFunil, jornadaNoPeriodo } from '../../../src/lib/dashboard/funilJornadaNoPeriodo';
import type { ContagemFunil } from '../../../src/lib/dashboard/funilJornadaNoPeriodo';
import { linhasDaEtapa } from '../../../src/lib/dashboard/negociacoesDaEtapa';
import type { LinhaNegociacao } from '../../../src/lib/dashboard/negociacoesDaEtapa';
import { CHAVE_COM_PENDENCIA, CHAVE_EM_ANALISE, COLUNAS_VENDEDOR, etapaComPendencia, etapaEmAnalise, funilPorVendedor, totalDoFunil } from './funilPorVendedor';
import type { ColunaVendedor, LinhaVendedor } from './funilPorVendedor';
// Seletor e lista do Comercial do Painel Executivo: mesma conta e mesma lista nas duas telas.
import SeletorContagemFunil from '../../Dashboard/PainelExecutivo/components/SeletorContagemFunil';
import ListaNegociacoesModal from '../../Dashboard/PainelExecutivo/sections/comercial/ListaNegociacoesModal';
import { planilhaNegociacoes } from '../../Dashboard/PainelExecutivo/sections/comercial/planilhaNegociacoes';
import { baixarPlanilhas } from '../../Financeiro/PainelExecutivo/lib/baixarPlanilhas';
import { slugArquivo } from '../../Financeiro/PainelExecutivo/lib/planilhasPainel';
import { useToast } from '../../../src/contexts/ToastContext';
import FunilPorVendedorBlock from './blocks/FunilPorVendedorBlock';
import { embedToArray } from '../../../src/lib/utils';
import { CHANNELS, getChannel } from '../../../src/lib/canalComercial';
import { FUNCOES_COM_RODIZIO } from '../../../src/lib/departamentos';
import { supervisionaComercial } from '../../../src/lib/supervisaoComercial';
import { CustomRange, Periodo, PERIODOS, inPeriodo, periodoResolvesToSingleMonth, resolvePeriodoAnteriorRange } from '../../../src/lib/periodoFiltro';
import { linhasAprovadasNoFechamento, aprovacaoAindaValida } from '../../../src/lib/fechamentoMensal';
import { exportAnalyticsExcel, AnalyticsExportInput } from '../../../src/lib/analyticsComercialExport';
import { exportAnalyticsPdf } from '../../../src/lib/analyticsComercialPdf';
import { copyToClipboard } from '../../../src/lib/clipboardShare';
import { NegRow, RankingSortKey, RANKING_SORT_OPTIONS, StatusBucket, STATUS_BUCKET_LABELS } from './types';
import { INDICADOR_ENERGIA_COMERCIAL } from '../../../src/lib/dashboard/metasMensais';
import { mesIso, metaDoTimeNoMes, origemDaMetaDoTime, somarMetasIndividuais } from '../../../src/lib/metasComerciais';
import { useMetasMensais } from '../../Dashboard/PainelExecutivo/sections/metas/useMetasMensais';
import { useKpisDepartamento } from '../../Dashboard/PainelExecutivo/sections/ceo/useCeoDados';
import { useMetasVendedores } from '../../VisaoGeral/useGestaoComercial';
import { statusPanelBucket } from './statusPanelBuckets';
import { computeRankingConsultores, rankingKeyValue, RANKING_VALUE_FORMAT } from './rankingConsultores';
import MultiSelectDropdown from '../../../src/components/MultiSelectDropdown';
import StatusPanel from './blocks/StatusPanel';
import SlaAtendimentoBlock from './blocks/SlaAtendimentoBlock';
import ChannelPerformanceBlock from './blocks/ChannelPerformanceBlock';
import MetaRealizadoBlock from './blocks/MetaRealizadoBlock';
import NegotiationDetailModal from '../components/NegotiationDetails/NegotiationDetailModal';
import ExportPanel from './export/ExportPanel';
import ResumoVisualCard, { ResumoVisualData } from './export/ResumoVisualCard';
import { calcularComissaoParceiro, formatCurrency, getTarifaVigente } from '../../../src/lib/comissaoUtils';
import { REGRA_BONIFICACAO, calcularBonificacao, limitesFaixa } from '../../../src/lib/bonificacaoParceiro';
import { calcularBonificacaoDasVendas, donoDaVenda, ehRegraNova, mesAceite, rotuloFaixaMes } from '../../../src/lib/bonificacaoParceiroFolha';
import type { UcDaVenda } from '../../../src/lib/bonificacaoParceiroFolha';
import { useCodigoIndicacao } from '../../../src/hooks/useCodigoIndicacao';
import { LinkCaptacaoParceiro } from '../../../src/components/LinkCaptacaoParceiro';

/** Card clicável vira <button> (abre a lista das negociações que ele soma), para
 *  funcionar também pelo teclado; sem clique, continua um bloco simples. */
const CardClicavel: React.FC<{ onClick?: () => void; label: string; className: string; children: React.ReactNode }> = ({ onClick, label, className, children }) =>
    onClick ? (
        <button
            type="button"
            onClick={onClick}
            title="Ver as negociações"
            aria-label={`${label}: ver as negociações`}
            className={`${className} w-full text-left cursor-pointer hover:border-primary/40 hover:shadow-md transition-all focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/40`}
        >
            {children}
        </button>
    ) : (
        <div className={className}>{children}</div>
    );

const KpiCard: React.FC<{ label: string; value: string; icon: React.ReactNode; tone?: string; sublabel?: string; onClick?: () => void }> = ({ label, value, icon, tone = 'text-gray-900 dark:text-white', sublabel, onClick }) => (
    <CardClicavel
        onClick={onClick}
        label={label}
        className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4 flex items-start justify-between"
    >
        <div>
            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">{label}</p>
            <p className={`text-2xl font-bold mt-1 ${tone}`}>{value}</p>
            {sublabel && <p className="text-[10px] text-gray-400 mt-0.5">{sublabel}</p>}
        </div>
        <div className="text-gray-300 dark:text-gray-600">{icon}</div>
    </CardClicavel>
);

// KPI com um chip de kWh embaixo — usado na lista simplificada de parceiro/vendedor_parceiro
const KpiCardKwh: React.FC<{ label: string; value: string; kwh: string; icon: React.ReactNode; attention?: boolean; onClick?: () => void }> = ({ label, value, kwh, icon, attention, onClick }) => (
    <CardClicavel
        onClick={onClick}
        label={label}
        className={`rounded-2xl border p-4 shadow-sm ${attention ? 'bg-red-950/25 border-red-500/30 dark:bg-red-950/30' : 'bg-white dark:bg-gray-800 border-gray-100 dark:border-gray-700'}`}
    >
        <div className="flex items-start justify-between">
            <p className={`text-[11px] font-semibold uppercase tracking-wide ${attention ? 'text-red-200/90' : 'text-gray-500 dark:text-gray-400'}`}>{label}</p>
            <div className={attention ? 'text-red-300/70' : 'text-gray-300 dark:text-gray-600'}>{icon}</div>
        </div>
        <p className={`text-2xl font-bold mt-1 ${attention ? 'text-red-50' : 'text-gray-900 dark:text-white'}`}>{value}</p>
        <div className={`inline-flex items-center gap-1.5 mt-2.5 px-2.5 py-1 rounded-full text-[11px] font-bold border ${attention ? 'border-red-400/40 text-red-100' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-300'}`}>
            <Zap size={11} />
            {kwh}
        </div>
    </CardClicavel>
);

const SEG_TABS = ['Distribuição', 'Conversão', 'Motivos de Perda'] as const;

/** Data local em YYYY-MM-DD — para virar o `customRange` de um período 'personalizado'. */
const diaIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

/** A lista que abre ao clicar num card, numa célula da tabela por vendedor ou numa barra
 *  do funil. `chaveData` é a etapa cuja data a lista mostra (ver linhasDaEtapa). */
interface ListaAberta {
    titulo: string;
    descricao: string;
    chaveData: string;
    rows: EventFunnelRow[];
}

/** Chip de tendência vs. período anterior — verde/vermelho conforme a direção esperada
 * (maior é melhor pra todo KPI de Visão Geral hoje). */
const TrendChip: React.FC<{ atual: number; anterior: number; fmt: (v: number) => string }> = ({ atual, anterior, fmt }) => {
    if (anterior === 0 && atual === 0) return <span className="text-[10px] text-gray-400">sem variação</span>;
    const deltaPct = anterior > 0 ? ((atual - anterior) / anterior) * 100 : 100;
    const up = atual >= anterior;
    const Icon = atual === anterior ? Minus : up ? TrendingUp : TrendingDown;
    const color = atual === anterior ? 'text-gray-400' : up ? 'text-green-600 dark:text-green-400' : 'text-red-500';
    return (
        <span className={`inline-flex items-center gap-1 text-[10px] font-bold ${color}`}>
            <Icon size={11} />
            {deltaPct >= 0 ? '+' : ''}{deltaPct.toFixed(0)}% vs. {fmt(anterior)}
        </span>
    );
};

const AnalyticsComercialView: React.FC = () => {
    const { profile, refreshProfile } = useAuth();
    const [rows, setRows] = useState<NegRow[]>([]);
    const [profiles, setProfiles] = useState<Profile[]>([]);
    const [loading, setLoading] = useState(true);
    const [diasCongelamento, setDiasCongelamento] = useState(12);

    const [periodo, setPeriodo] = useState<Periodo>('tudo');
    const [customRange, setCustomRange] = useState<CustomRange>({ inicio: '', fim: '' });
    const [selectedResponsibles, setSelectedResponsibles] = useState<string[]>([]);
    const [selectedCanais, setSelectedCanais] = useState<string[]>([]);
    const [selectedCidades, setSelectedCidades] = useState<string[]>([]);
    const [selectedOrigens, setSelectedOrigens] = useState<string[]>([]);
    const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
    const [segTab, setSegTab] = useState<typeof SEG_TABS[number]>('Distribuição');
    const [tarifas, setTarifas] = useState<TarifaEnergia[]>([]);
    const [rankingSortKey, setRankingSortKey] = useState<RankingSortKey>('kwh_aprovado');
    const [detailId, setDetailId] = useState<number | null>(null);
    // 'evento' = cada etapa pela data dela; 'jornada' = só quem teve todas as etapas até
    // a do card dentro do período. Mesmo seletor do Comercial do Painel Executivo.
    const [contagem, setContagem] = useState<ContagemFunil>('evento');
    // Lista de negociações em modal (a mesma do Painel Executivo) — abre pelos cards, pela
    // tabela por vendedor e pelas barras/legendas do Funil Macro.
    const [lista, setLista] = useState<ListaAberta | null>(null);
    const [exportandoLista, setExportandoLista] = useState(false);
    const { addToast } = useToast();
    const [refetchTick, setRefetchTick] = useState(0);
    const [resumoCopiado, setResumoCopiado] = useState(false);
    const [exportingImage, setExportingImage] = useState(false);
    const resumoVisualRef = useRef<HTMLDivElement>(null);
    // Só existe durante a exportação — ver handleExportImage
    const [resumoVisualData, setResumoVisualData] = useState<ResumoVisualData | null>(null);

    // Quem supervisiona o Comercial (#6521) vê o Analytics da equipe (Closers, BDRs e ele mesmo),
    // com filtros e ranking, e não a visão individual de vendedor.
    const supervisiona = profile?.role === 'vendedor' && supervisionaComercial(profile);
    const isVendedor = (profile?.role === 'vendedor' || profile?.role === 'vendedor_parceiro') && !supervisiona;
    // Parceiro/vendedor parceiro: sem métricas de gestão comercial (perda, ranking, insights, segmento)
    const isParceiroRole = profile?.role === 'parceiro' || profile?.role === 'vendedor_parceiro';
    // Só o parceiro (dono da carteira) recebe comissão — vendedor_parceiro não vê o simulador
    const isParceiro = profile?.role === 'parceiro';
    // Meta x Realizado: só gestão vê (mesmo gate do botão "Relatórios" -> Performance Comercial no Kanban)
    const isGestor = profile?.role === 'gerente' || profile?.role === 'adm';

    // Link de indicação — migrou do Kanban pra cá; o banner de dados bancários ficou no Kanban
    const { gerandoCodigo, linkCopiado, linkIndicacao, handleCopiarLink, handleCompartilharWhatsapp } =
        useCodigoIndicacao(profile, isParceiroRole, refreshProfile);

    useEffect(() => {
        const fetchAll = async () => {
            setLoading(true);
            try {
                // Escopo de responsável conforme o papel do usuário (null = todos)
                let singleResp: string | null = null;
                let respScope: string[] | null = null;
                if (isVendedor && profile) {
                    singleResp = profile.id;
                } else if (supervisiona && profile) {
                    const { data: equipe } = await supabase.from('profiles').select('id')
                        .in('funcao_comercial', [...FUNCOES_COM_RODIZIO]).eq('status', 'active');
                    respScope = [...new Set([profile.id, ...(equipe?.map(m => m.id) || [])])];
                } else if (profile?.role === 'parceiro') {
                    const { data: managed } = await supabase.from('profiles').select('id').eq('parceiro_id', profile.id);
                    respScope = [profile.id, ...(managed?.map(m => m.id) || [])];
                } else if (profile?.role === 'gerente_parcerias') {
                    const { data: parceiroUsers } = await supabase.from('profiles').select('id').in('role', ['parceiro', 'vendedor_parceiro']);
                    respScope = [profile.id, ...(parceiroUsers?.map(m => m.id) || [])];
                }

                // profiles + setting podem ser buscados em paralelo com a paginação
                const profilesPromise = supabase.from('profiles').select('*').neq('role', 'consumidor');
                const settingPromise = supabase.from('system_settings').select('value').eq('key', 'geo_dias_congelamento').maybeSingle();

                // Busca paginada: o Supabase/PostgREST limita cada resposta a 1000 linhas.
                // Com a base já acima de 1000 negociações, uma query única truncava os
                // totais de kWh silenciosamente — paginamos por id até esgotar.
                const PAGE = 1000;
                const buildQuery = () => {
                    let q = supabase
                        .from('negociacoes')
                        .select('id, status, stage, segmento, cliente_nome, consumo_medio_kwh, responsavel_id, motivo_perda, created_at, data_assinatura_termo, ultima_atividade_at, cidade, origem, url_simulacao, url_termo, cpf_cnpj, tipo_cliente, termo_reprovado_em, termo_reprovado_motivo, termo_reprovado_aproximado, analise_cadastral(decisao_final, decisao_final_at)')
                        .order('id', { ascending: true });
                    if (singleResp) q = q.eq('responsavel_id', singleResp);
                    else if (respScope) q = q.in('responsavel_id', respScope);
                    return q;
                };
                const allRows: NegRow[] = [];
                for (let page = 0; ; page++) {
                    const from = page * PAGE;
                    const { data, error } = await buildQuery().range(from, from + PAGE - 1);
                    if (error) throw error;
                    const batch = (data || []) as any[];
                    // embed 1→1 (UNIQUE em negociacao_id) vem como objeto — normaliza pra array,
                    // mesmo padrão de KanbanBoard.tsx.
                    batch.forEach(n => { n.analise_cadastral = embedToArray(n.analise_cadastral); });
                    allRows.push(...(batch as NegRow[]));
                    if (batch.length < PAGE) break;
                }

                const [{ data: profs }, { data: setting }] = await Promise.all([profilesPromise, settingPromise]);
                setRows(allRows);
                if (profs) setProfiles(profs);
                const dias = parseInt(String(setting?.value || '').replace(/\D/g, ''), 10);
                if (Number.isFinite(dias) && dias > 0) setDiasCongelamento(dias);

                // Tarifa vigente — só necessária pro simulador de comissão do parceiro
                if (profile?.role === 'parceiro') {
                    const tarifasData = await api.folhaPagamento.getTarifas();
                    setTarifas(tarifasData);
                }
            } catch (err) {
                console.error('Erro ao carregar analytics:', err);
            } finally {
                setLoading(false);
            }
        };
        if (profile) fetchAll();
    }, [profile?.id, refetchTick]);

    const canalOf = (respId?: string | null) => getChannel(profiles.find(p => p.id === respId));

    // Opções disponíveis pros filtros de cidade/origem, derivadas da base carregada.
    const cidadeOptions = useMemo(() => Array.from(new Set(rows.map(r => r.cidade?.trim() || 'Sem cidade'))).sort(), [rows]);
    const origemOptions = useMemo(() => Array.from(new Set(rows.map(r => r.origem?.trim() || 'Não informado'))).sort(), [rows]);
    const statusOptions = useMemo(() => Array.from(new Set(rows.map(r => r.status))).map(s => STATUS_LABELS[s] || s).sort(), [rows]);
    const statusLabelToValue = useMemo(() => {
        const map: Record<string, string> = {};
        Array.from(new Set(rows.map(r => r.status))).forEach(s => { map[STATUS_LABELS[s] || s] = s; });
        return map;
    }, [rows]);

    const filtered = useMemo(() => rows.filter(r => {
        if (!inPeriodo(r.created_at, periodo, customRange)) return false;
        if (selectedResponsibles.length > 0 && !selectedResponsibles.includes(String(r.responsavel_id || ''))) return false;
        if (selectedCanais.length > 0 && !selectedCanais.includes(canalOf(r.responsavel_id))) return false;
        if (selectedCidades.length > 0 && !selectedCidades.includes(r.cidade?.trim() || 'Sem cidade')) return false;
        if (selectedOrigens.length > 0 && !selectedOrigens.includes(r.origem?.trim() || 'Não informado')) return false;
        if (selectedStatuses.length > 0 && !selectedStatuses.includes(r.status)) return false;
        return true;
    }), [rows, periodo, customRange, selectedResponsibles, selectedCanais, selectedCidades, selectedOrigens, selectedStatuses, profiles]);

    // Igual a `filtered`, mas sem o filtro de período — usado pelo bloco de Meta x
    // Realizado e pelo de Canal, que resolvem seu próprio recorte de mês/série.
    const filteredExcetoPeriodo = useMemo(() => rows.filter(r => {
        if (selectedResponsibles.length > 0 && !selectedResponsibles.includes(String(r.responsavel_id || ''))) return false;
        if (selectedCanais.length > 0 && !selectedCanais.includes(canalOf(r.responsavel_id))) return false;
        if (selectedCidades.length > 0 && !selectedCidades.includes(r.cidade?.trim() || 'Sem cidade')) return false;
        if (selectedOrigens.length > 0 && !selectedOrigens.includes(r.origem?.trim() || 'Não informado')) return false;
        if (selectedStatuses.length > 0 && !selectedStatuses.includes(r.status)) return false;
        return true;
    }), [rows, selectedResponsibles, selectedCanais, selectedCidades, selectedOrigens, selectedStatuses, profiles]);

    // Mês-calendário único resolvido pelo período atual — só quando não-nulo o bloco de
    // Meta x Realizado consegue calcular "realizado" (linhasAprovadasNoFechamento é por mês/ano).
    const mesUnico = useMemo(() => periodoResolvesToSingleMonth(periodo, customRange), [periodo, customRange]);
    const mesLabel = mesUnico
        ? new Date(mesUnico.ano, mesUnico.mes - 1, 1).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
        : '';

    // ── Metas: uma fonte por nível (src/lib/metasComerciais.ts) ──
    // Meta do time: `metas_mensais` (com_kwh), a mesma linha da aba Metas do Painel Executivo.
    // Meta por pessoa: a da Folha de Pagamento. Nada fica fixo no código.
    const metasMensais = useMetasMensais();
    const { kpis: kpisSalvos } = useKpisDepartamento();
    const hoje = new Date();
    const mesDaMeta = mesUnico ? mesIso(mesUnico.mes, mesUnico.ano) : mesIso(hoje.getMonth() + 1, hoje.getFullYear());
    const metaDoTime = useMemo(() => metaDoTimeNoMes(
        metasMensais.metas,
        kpisSalvos.find(k => k.calculo_automatico === INDICADOR_ENERGIA_COMERCIAL && k.status === 'validado'),
        mesDaMeta,
    ), [metasMensais.metas, kpisSalvos, mesDaMeta]);
    const idsDaEquipe = useMemo(() => (isGestor ? profiles.map(p => String(p.id)) : []), [isGestor, profiles]);
    const metasDaFolha = useMetasVendedores(idsDaEquipe, mesUnico?.mes ?? hoje.getMonth() + 1, mesUnico?.ano ?? hoje.getFullYear());
    // Meta por canal = soma das metas da Folha de quem é do canal (Performance por Canal e alertas).
    const metasMap = useMemo(() => {
        const m: Record<string, number> = {};
        CHANNELS.forEach(ch => { m[ch] = somarMetasIndividuais(metasDaFolha, profiles.filter(p => getChannel(p) === ch).map(p => String(p.id))); });
        return m;
    }, [metasDaFolha, profiles]);
    // Com filtro de canal ou pessoa, a meta do bloco é a soma da Folha de quem está no recorte;
    // sem filtro, é a meta do time.
    const recorteFiltrado = selectedCanais.length > 0 || selectedResponsibles.length > 0;
    const idsDoRecorte = useMemo(() => (selectedResponsibles.length > 0
        ? selectedResponsibles
        : profiles.filter(p => selectedCanais.includes(getChannel(p))).map(p => String(p.id))
    ), [selectedResponsibles, selectedCanais, profiles]);
    const metaDoRecorte = recorteFiltrado ? somarMetasIndividuais(metasDaFolha, idsDoRecorte) : (metaDoTime?.valor ?? 0);
    const origemMetaDoRecorte = recorteFiltrado ? null : (metaDoTime ? origemDaMetaDoTime(metaDoTime) : null);
    // Só adm grava a meta do time: a mesma regra (e a mesma RLS) da aba Metas do Painel Executivo.
    const podeEditarMetaDoTime = profile?.role === 'adm';
    const salvarMetaDoTime = async (valor: number, observacao: string) => {
        if (!mesUnico) return;
        await metasMensais.salvar(INDICADOR_ENERGIA_COMERCIAL, mesIso(mesUnico.mes, mesUnico.ano), valor, observacao);
        addToast('Meta do Comercial salva. Ela vale também no Painel Executivo e na Visão Geral.', 'success');
    };

    // Canais relevantes pro cálculo de Meta — respeita tanto o filtro de Canal quanto o de
    // Responsável. Sem isso, filtrar por uma pessoa (ex.: Julia, Comercial) mantinha a
    // meta somando TODOS os canais, comparando o realizado de uma pessoa contra a meta da
    // empresa inteira. Canal explícito tem prioridade; sem ele, deriva dos responsáveis
    // selecionados; sem nenhum dos dois, considera todos os canais com meta configurada.
    const canaisEfetivos = useMemo(() => {
        if (selectedCanais.length > 0) return selectedCanais;
        if (selectedResponsibles.length > 0) {
            return Array.from(new Set(selectedResponsibles.map(id => canalOf(id))));
        }
        return Object.keys(metasMap);
    }, [selectedCanais, selectedResponsibles, profiles, metasMap]);

    // Mesma janela de duração do período atual, imediatamente anterior — só pra
    // comparação/tendência do bloco de Visão Geral. Aplica os mesmos filtros não-período
    // e a MESMA conta dos cards (etapa pela data dela, ou só o que aconteceu no período).
    // Antes, "Aprovados" comparava o card (por data da aprovação) com quem foi CRIADO no
    // período anterior e está aprovado hoje — duas perguntas diferentes na mesma linha.
    // A janela vira dias inteiros: nas janelas móveis (7d, 30d...) a ponta pode andar
    // algumas horas, o que não muda a leitura de tendência.
    const computedAnterior = useMemo(() => {
        const range = resolvePeriodoAnteriorRange(periodo, customRange);
        if (!range) return null;
        const customAnterior: CustomRange = { inicio: diaIso(range.start), fim: diaIso(range.end) };
        const etapas = aplicarContagemFunil(
            computeEventFunnel(filteredExcetoPeriodo, 'personalizado', customAnterior),
            contagem, 'personalizado', customAnterior,
        );
        const de = (k: string) => etapas.find(s => s.key === k);
        const total = de('prospeccao')?.leads ?? 0;
        const aprovados = de('termo_aprovado')?.leads ?? 0;
        return {
            total,
            aprovados,
            kwhAprovado: de('termo_aprovado')?.kwh ?? 0,
            conversaoGeral: total > 0 ? (aprovados / total) * 100 : 0,
        };
    }, [filteredExcetoPeriodo, periodo, customRange, contagem]);

    const computed = useMemo(() => {
        const total = filtered.length;
        const perdidos = filtered.filter(r => r.status === 'perdido');
        const isCongelado = (r: NegRow) => {
            if (['perdido', 'termo_aprovado', 'termo_assinado', 'ganho'].includes(r.status)) return false;
            const t = r.ultima_atividade_at ? new Date(r.ultima_atividade_at).getTime() : new Date(r.created_at).getTime();
            return (Date.now() - t) > diasCongelamento * 86400000;
        };
        const congelados = filtered.filter(isCongelado);
        const naoPerdidos = filtered.filter(r => r.status !== 'perdido');
        // Ativos = nem perdido nem congelado (congelado já exclui perdido) — mesma conta do card.
        const ativosRows = naoPerdidos.filter(r => !isCongelado(r));

        // Funil por DATA DO EVENTO — cada etapa conta pela data em que ela própria
        // aconteceu (não pela data de criação do lead), independente do status atual da
        // negociação. Base: filteredExcetoPeriodo (mesmos filtros de responsável/canal/
        // cidade/origem/status, sem o filtro de período) — cada etapa aplica seu próprio
        // inPeriodo internamente (ver eventFunnel.ts). Prospecção usa created_at como sua
        // própria "data de evento", então continua batendo com "Total de Leads".
        // Consequência aceita: o funil deixa de ser garantidamente decrescente — uma etapa
        // pode ter mais itens que a anterior, porque vêm de grupos de negociações diferentes.
        //
        // Com a contagem 'jornada' ("Só o que aconteceu no mês"), cada etapa fica só com
        // quem teve todas as etapas até ela dentro do período — aí o funil volta a ser
        // decrescente. Termo Reprovado (saída) entra para a tabela por vendedor e a lista,
        // mas fica fora do gráfico e das taxas etapa-a-etapa.
        const etapasEvento = computeEventFunnel(filteredExcetoPeriodo, periodo, customRange, { incluirSaidas: true });
        const etapasContadas = aplicarContagemFunil(etapasEvento, contagem, periodo, customRange);
        const eventStages = etapasContadas.filter(s => s.tipo !== 'saida');
        const reprovadoStage = etapasContadas.find(s => s.key === 'termo_reprovado')!;
        const stageByKey: Record<string, typeof eventStages[number]> = Object.fromEntries(eventStages.map(s => [s.key, s]));
        // Em validação = só a fila da analista; quem já foi analisado e ficou com pendência
        // vai para o card à parte e não infla a fila (src/lib/validacaoDados.ts).
        const emAnaliseStage = etapaEmAnalise(stageByKey['termo_assinado']);
        const comPendenciaStage = etapaComPendencia(stageByKey['termo_assinado']);
        const simulacoes = stageByKey['simulacao'].leads;
        const assinados = stageByKey['termo_assinado'].leads;
        const aprovados = stageByKey['termo_aprovado'].leads;
        const kwhAssinado = stageByKey['termo_assinado'].kwh;
        const kwhAprovado = stageByKey['termo_aprovado'].kwh;
        const kwhPotencial = stageByKey['prospeccao'].kwh;
        const aprovadosRows = stageByKey['termo_aprovado'].rows;

        const kwhPerdido = perdidos.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
        // Aguardando assinatura / Reprovado — status atual exato (não cumulativo pelo funil)
        const aguardandoAssinaturaRows = filtered.filter(r => r.status === 'termo_enviado');
        const aguardandoAssinatura = aguardandoAssinaturaRows.length;
        const kwhAguardandoAssinatura = aguardandoAssinaturaRows.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
        const reprovadosRows = filtered.filter(r => r.status === 'termo_reprovado');
        const reprovados = reprovadosRows.length;
        const kwhReprovado = reprovadosRows.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
        const ticketMedio = aprovadosRows.length > 0 ? kwhAprovado / aprovadosRows.length : 0;
        const conversaoGeral = total > 0 ? (aprovados / total) * 100 : 0;
        const taxaCongelamento = naoPerdidos.length > 0 ? (congelados.length / naoPerdidos.length) * 100 : 0;

        // Volume energético e quantidade de leads por etapa, já calculados acima. Duas
        // séries de taxa etapa-a-etapa: por lead e por kWh — como cada etapa agora vem de
        // um grupo diferente (por evento), essas taxas são uma proporção entre dois totais
        // independentes, não mais "% que avançou" no sentido estrito de coorte.
        const kwhPorEtapa: Record<string, number> = Object.fromEntries(eventStages.map(s => [s.key, s.kwh]));
        const leadStepRates: Array<{ from: string; to: string; rate: number }> = [];
        const kwhStepRates: Array<{ from: string; to: string; rate: number }> = [];
        for (let i = 0; i < eventStages.length - 1; i++) {
            const from = eventStages[i];
            const to = eventStages[i + 1];
            leadStepRates.push({ from: from.label, to: to.label, rate: from.leads > 0 ? (to.leads / from.leads) * 100 : 0 });
            kwhStepRates.push({ from: from.label, to: to.label, rate: from.kwh > 0 ? (to.kwh / from.kwh) * 100 : 0 });
        }

        // Motivos de perda (geral)
        const motivos: Record<string, number> = {};
        perdidos.forEach(r => { const m = r.motivo_perda || 'Não informado'; motivos[m] = (motivos[m] || 0) + 1; });
        const motivosList = Object.entries(motivos).sort((a, b) => b[1] - a[1]);

        // Assinados/Aprovados por evento, reusados no Ranking e no insight "Canal abaixo da
        // meta" abaixo — já calculados acima (stageByKey), sem duplicar a lógica.
        const assinadosEventoRows = stageByKey['termo_assinado'].rows;
        const aprovadosEventoRows = stageByKey['termo_aprovado'].rows;

        // Ranking de consultores — critério padrão (kWh aprovado), com seletor pra reordenar
        // por qualquer um dos 5 indicadores. `leads`/`conversao` continuam de coorte (quem
        // entrou no período, quanto converteu); `assinados`/`aprovados`/volumes vêm das listas
        // por evento acima (caso do Rafael: assinou 3 termos de clientes criados em meses
        // anteriores). Lógica extraída pra rankingConsultores.ts (testável fora do componente).
        const ranking = computeRankingConsultores(filtered, assinadosEventoRows, aprovadosEventoRows, rankingSortKey);

        // Funil por segmento — mesmo princípio do funil macro: agrupa a partir de
        // `filteredExcetoPeriodo` e cada etapa aplica seu próprio filtro de data de evento.
        const segmentos: Record<string, NegRow[]> = {};
        filteredExcetoPeriodo.forEach(r => {
            const s = r.segmento?.trim() || 'Sem segmento';
            (segmentos[s] = segmentos[s] || []).push(r);
        });
        const segData = Object.entries(segmentos)
            .map(([seg, list]) => {
                const stages = aplicarContagemFunil(computeEventFunnel(list, periodo, customRange), contagem, periodo, customRange);
                const byKey: Record<string, typeof stages[number]> = Object.fromEntries(stages.map(s => [s.key, s]));
                const total = byKey['prospeccao'].leads;
                return {
                    segmento: seg,
                    total,
                    prospeccao: byKey['prospeccao'].leads,
                    simulacao: byKey['simulacao'].leads,
                    termo_enviado: byKey['termo_enviado'].leads,
                    termo_assinado: byKey['termo_assinado'].leads,
                    termo_aprovado: byKey['termo_aprovado'].leads,
                    conversao: total > 0 ? (byKey['termo_aprovado'].leads / total) * 100 : 0,
                    kwh: byKey['prospeccao'].kwh,
                };
            })
            .sort((a, b) => b.total - a.total);

        // Motivos de perda por segmento
        const motivosPorSeg: Record<string, Record<string, number>> = {};
        perdidos.forEach(r => {
            const s = r.segmento?.trim() || 'Sem segmento';
            const m = r.motivo_perda || 'Não informado';
            motivosPorSeg[s] = motivosPorSeg[s] || {};
            motivosPorSeg[s][m] = (motivosPorSeg[s][m] || 0) + 1;
        });

        // Insights heurísticos
        const insights: Array<{ tone: 'warn' | 'good' | 'info'; text: string }> = [];
        if (leadStepRates.length > 0 && total > 0) {
            const valid = leadStepRates.filter(s => s.rate > 0 || true);
            const pior = valid.reduce((min, s) => (s.rate < min.rate ? s : min), valid[0]);
            const melhor = valid.reduce((max, s) => (s.rate > max.rate ? s : max), valid[0]);
            if (pior) insights.push({ tone: 'warn', text: `Gargalo em ${pior.from} → ${pior.to}: apenas ${fmtPct(pior.rate)} avançam nessa etapa.` });
            if (melhor && melhor !== pior && melhor.rate > 0) insights.push({ tone: 'good', text: `Forte em ${melhor.from} → ${melhor.to}: ${fmtPct(melhor.rate)} de conversão. Mantenha o processo.` });
        }
        if (congelados.length > 0) insights.push({ tone: 'warn', text: `${congelados.length} leads congelados (sem atividade há +${diasCongelamento} dias) — taxa de congelamento de ${fmtPct(taxaCongelamento)}.` });
        if (motivosList.length > 0) {
            const [topMotivo, qtd] = motivosList[0];
            insights.push({ tone: 'info', text: `Principal motivo de perda: "${topMotivo}" (${qtd} de ${perdidos.length} perdas).` });
        }

        // Backlog de pendências da validação — mesma base do Painel de Status (snapshot atual,
        // não coorte do período), pra não divergir do painel.
        const pendenciaCount = filteredExcetoPeriodo.filter(r => statusPanelBucket(r) === 'com_pendencia').length;
        if (pendenciaCount > 0) {
            insights.push({ tone: 'warn', text: `${pendenciaCount} ${pendenciaCount === 1 ? 'negociação está' : 'negociações estão'} com pendência na validação de dados, esperando documento, garantia ou confirmação do cliente.` });
        }

        // Concentração geográfica — cidade com participação desproporcional no funil
        if (total >= 10) {
            const porCidade: Record<string, number> = {};
            filtered.forEach(r => { const c = r.cidade?.trim() || 'Sem cidade'; porCidade[c] = (porCidade[c] || 0) + 1; });
            const [topCidade, qtdCidade] = Object.entries(porCidade).sort((a, b) => b[1] - a[1])[0] || [];
            const pctCidade = topCidade ? (qtdCidade / total) * 100 : 0;
            if (topCidade && topCidade !== 'Sem cidade' && pctCidade >= 40) {
                insights.push({ tone: 'info', text: `${fmtPct(pctCidade)} dos leads do período estão concentrados em ${topCidade} — considere diversificar a prospecção geográfica.` });
            }
        }

        // Canal abaixo da meta (só gerente/adm com mês-calendário único resolvido).
        // Realizado por evento (mesma base da Meta), pra não misturar coorte com evento.
        // Só avalia canais em `canaisEfetivos` — senão, filtrando por uma pessoa que só
        // vende num canal, os outros apareciam com alerta de "0% da meta" sem sentido.
        if (isGestor && mesUnico) {
            const canalOfLocal = (id?: string | null) => getChannel(profiles.find(p => p.id === id));
            // A meta é de kWh aprovado no mês, venha de quando vier: usa sempre a contagem
            // pela data da aprovação, mesmo com "Só o que aconteceu no mês" ligado.
            const aprovadosParaMeta = etapasEvento.find(s => s.key === 'termo_aprovado')?.rows ?? [];
            CHANNELS.filter(ch => canaisEfetivos.includes(ch)).forEach(ch => {
                const metaCh = metasMap[ch] || 0;
                if (metaCh <= 0) return;
                const realizadoCh = aprovadosParaMeta
                    .filter(r => canalOfLocal(r.responsavel_id) === ch)
                    .reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
                const pctCh = (realizadoCh / metaCh) * 100;
                if (pctCh < 70) {
                    insights.push({ tone: 'warn', text: `Canal ${ch} está em ${fmtPct(pctCh)} da meta do mês (${fmtKwh(realizadoCh)} de ${fmtKwh(metaCh)}).` });
                }
            });
        }

        // Funil por vendedor — as MESMAS etapas dos cards (já com a contagem escolhida),
        // então a linha de total da tabela bate com eles.
        const nomeDe = (id: string) => profiles.find(p => String(p.id) === id)?.full_name;
        const vendedores = funilPorVendedor([...eventStages, emAnaliseStage, comPendenciaStage, reprovadoStage], nomeDe);

        return {
            total, perdidos: perdidos.length, congelados: congelados.length,
            ativos: ativosRows.length,
            simulacoes, assinados, aprovados, conversaoGeral, taxaCongelamento,
            kwhPotencial, kwhAprovado, kwhAssinado, kwhPerdido, ticketMedio,
            aguardandoAssinatura, kwhAguardandoAssinatura, reprovados, kwhReprovado,
            kwhPorEtapa, kwhStepRates, leadStepRates, funnelStages: eventStages,
            motivosList, ranking, segData, motivosPorSeg, insights,
            emAnaliseStage, comPendenciaStage, reprovadoStage, vendedores, vendedoresTotal: totalDoFunil(vendedores),
            // Linhas dos cards que não são etapa do funil (coorte: criadas no período).
            listas: { criadas: filtered, ativos: ativosRows, congelados, perdidos, aguardandoAssinatura: aguardandoAssinaturaRows, reprovadosStatus: reprovadosRows },
        };
    }, [filtered, filteredExcetoPeriodo, periodo, customRange, contagem, diasCongelamento, rankingSortKey, isGestor, mesUnico, profiles, metasMap, canaisEfetivos]);

    const respNome = (id: string) => {
        const p = profiles.find(pr => String(pr.id) === id);
        return p?.full_name || (id === 'sem' ? 'Sem responsável' : '—');
    };

    // Bonificação do parceiro, regra de 26/10/2026: vendas dele e da equipe
    // (vendedor_parceiro) aceitas neste mês. A lista do funil não traz as UCs, então
    // o desconto de cada uma é buscado à parte (poucas linhas: só as aceitas no mês).
    const vendasRegraNovaDoMes = useMemo(() => {
        if (!isParceiro || !profile?.id) return [] as NegRow[];
        const mesAtual = mesAceite(new Date());
        const perfilDe = new Map(profiles.map(p => [String(p.id), p]));
        return rows.filter(r => {
            const ac = r.analise_cadastral?.[0];
            if (!ac?.decisao_final_at || !['Aprovado', 'Aprovado com Ressalvas'].includes(ac.decisao_final || '')) return false;
            if (r.status === 'perdido' || r.status === 'termo_reprovado') return false;
            if (!ehRegraNova(ac.decisao_final_at) || mesAceite(ac.decisao_final_at) !== mesAtual) return false;
            return donoDaVenda(perfilDe.get(String(r.responsavel_id))) === profile.id;
        });
    }, [isParceiro, profile?.id, rows, profiles]);

    const idsRegraNovaKey = vendasRegraNovaDoMes.map(r => r.id).join(',');
    const [ucsRegraNova, setUcsRegraNova] = useState<Map<number, UcDaVenda[]> | null>(null);
    useEffect(() => {
        if (!idsRegraNovaKey) { setUcsRegraNova(new Map()); return; }
        let cancelado = false;
        setUcsRegraNova(null);
        (async () => {
            const { data, error } = await supabase
                .from('unidades_consumidoras')
                .select('negociacao_id, desconto_tarifa, grupo_tarifario')
                .in('negociacao_id', idsRegraNovaKey.split(',').map(Number));
            if (cancelado) return;
            if (error) { console.error('Erro ao buscar o desconto das UCs (bonificação):', error); return; }
            const porNeg = new Map<number, UcDaVenda[]>();
            for (const u of (data || []) as Array<UcDaVenda & { negociacao_id: number }>) {
                const lista = porNeg.get(Number(u.negociacao_id)) || [];
                lista.push(u);
                porNeg.set(Number(u.negociacao_id), lista);
            }
            setUcsRegraNova(porNeg);
        })();
        return () => { cancelado = true; };
    }, [idsRegraNovaKey]);

    // Simulador de bonificação do parceiro. Independente do filtro de período da
    // página (é sempre "este mês"). Dois números com bases diferentes:
    // - Total Ganho: o que a Folha paga pelas aprovadas do mês — aceites a partir de
    //   26/10/2026 pela regra nova (faixa da soma do mês, com o desconto de cada UC);
    //   as anteriores pela regra antiga (calcularComissaoParceiro), sem mudança.
    // - Potencial de Ganho: TODO o kWh ainda em negociação (não perdido/reprovado)
    //   este mês, pela regra nova com o plano padrão (20%) e uma faixa só, da soma —
    //   projeção do que a carteira renderia se tudo fosse aprovado.
    const comissaoSimulacao = useMemo(() => {
        if (!isParceiro || !profile?.id) return null;

        const tarifa = getTarifaVigente(tarifas, new Date().toISOString().slice(0, 10));
        const tarifaValor = tarifa?.valor ?? 0;

        const inicioMes = new Date(new Date().getFullYear(), new Date().getMonth(), 1);
        const doMesRef = (r: NegRow) => new Date(r.data_assinatura_termo || r.created_at) >= inicioMes;

        // Regra antiga: só o que foi aceito antes de 26/10/2026
        const aprovadosRegraAntiga = rows.filter(r =>
            r.status !== 'perdido' && r.status !== 'termo_reprovado' && isEffectivelyAtOrAfter(r, 'termo_aprovado') && doMesRef(r)
            && !ehRegraNova(r.analise_cadastral?.[0]?.decisao_final_at)
        );
        const totalRegraAntiga = aprovadosRegraAntiga.reduce((s, r) => {
            const consumo = r.consumo_medio_kwh || 0;
            return s + calcularComissaoParceiro(consumo, tarifaValor).valorBase;
        }, 0);

        const bonificacao = calcularBonificacaoDasVendas(vendasRegraNovaDoMes.map(r => ({
            negociacao_id: r.id,
            dono_id: profile.id,
            decisao_final_at: r.analise_cadastral?.[0]?.decisao_final_at || '',
            consumo_medio_kwh: r.consumo_medio_kwh,
            cpf_cnpj: r.cpf_cnpj,
            ucs: ucsRegraNova?.get(r.id) || [],
        })));
        const doMes = [...bonificacao.values()];
        const totalRegraNova = doMes.reduce((s, b) => s + b.valor, 0);
        const pixIRegraNova = doMes.reduce((s, b) => s + b.pix1, 0);
        const faixaDoMes = doMes[0] ? rotuloFaixaMes(doMes[0].faixa_mes, doMes[0].faixa_percentual, doMes[0].kwh_mes) : null;

        // "Em negociação": tudo que ainda está vivo este mês (não perdido nem reprovado),
        // incluindo o que já foi aprovado — é o volume total da carteira do mês.
        const emNegociacao = rows.filter(r => r.status !== 'perdido' && r.status !== 'termo_reprovado' && doMesRef(r));
        const kwhEmNegociacao = emNegociacao.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
        const projecao = calcularBonificacao(emNegociacao.map(r => ({ c: Number(r.consumo_medio_kwh) || 0, d: REGRA_BONIFICACAO.padrao })));

        return {
            comissaoTotal: totalRegraAntiga + totalRegraNova,
            carregandoDescontos: vendasRegraNovaDoMes.length > 0 && ucsRegraNova === null,
            vendasRegraNova: doMes.length,
            pixIRegraNova,
            faixaDoMes,
            kwhEmNegociacao,
            faixaPotencial: projecao.fx,
            potencialGanho: projecao.B,
        };
    }, [isParceiro, profile?.id, rows, tarifas, vendasRegraNovaDoMes, ucsRegraNova]);

    // Evolução de Simulações Propostas — últimos 6 meses, comparado com a média de
    // outros parceiros/vendedor_parceiro da rede (benchmark vem do server, nunca
    // expõe dados individuais de terceiros — só a média agregada).
    const [benchmarkSimulacoes, setBenchmarkSimulacoes] = useState<number[] | null>(null);

    useEffect(() => {
        if (!isParceiroRole) return;
        let cancelled = false;
        const fetchBenchmark = async () => {
            try {
                const { data, error } = await supabase.functions.invoke('get-evolucao-simulacoes');
                if (error) throw error;
                if (data?.success === false) throw new Error(data.error || 'Erro ao buscar benchmark de simulações.');
                if (!cancelled) setBenchmarkSimulacoes(data.data.benchmark);
            } catch (err) {
                const message = await getFunctionErrorMessage(err, 'Erro ao buscar benchmark de simulações.');
                console.error('Erro ao buscar benchmark de simulações:', message, err);
            }
        };
        fetchBenchmark();
        return () => { cancelled = true; };
    }, [isParceiroRole, profile?.id]);

    const evolucaoSimulacoes = useMemo(() => {
        if (!isParceiroRole) return null;
        const now = new Date();
        const meses = Array.from({ length: 6 }, (_, idx) => {
            const i = 5 - idx;
            const start = new Date(now.getFullYear(), now.getMonth() - i, 1);
            const end = new Date(start.getFullYear(), start.getMonth() + 1, 1);
            const label = start.toLocaleDateString('pt-BR', { month: 'short', year: '2-digit' }).replace('.', '');
            return { label, start, end };
        });
        return meses.map((m, i) => ({
            name: m.label,
            voce: rows.filter(r => {
                const dt = new Date(r.created_at);
                return dt >= m.start && dt < m.end && isEffectivelyAtOrAfter(r, 'simulacao_realizada');
            }).length,
            media: benchmarkSimulacoes ? Math.round(benchmarkSimulacoes[i] * 10) / 10 : null,
        }));
    }, [isParceiroRole, rows, benchmarkSimulacoes]);

    const funnelChartData = computed.funnelStages.map(s => ({
        key: s.key,
        name: s.label,
        valor: s.leads,
        kwh: s.kwh,
        fill: s.color,
    }));

    // ─── Exportação executiva ───
    const periodoLabel = periodo === 'personalizado' && customRange.inicio && customRange.fim
        ? `${customRange.inicio} a ${customRange.fim}`
        : PERIODOS.find(p => p.value === periodo)?.label || periodo;

    // ─── Lista de negociações (modal do Painel Executivo) ───
    const soNoPeriodo = contagem === 'jornada';
    const recorte = mesUnico ? 'mês' : 'período';
    const textoContagem = soNoPeriodo
        ? `Só o que aconteceu no ${recorte}: a negociação só conta numa etapa se todas as etapas, da criação até ela, caíram dentro do ${recorte}. Cada etapa fica dentro da anterior.`
        : 'Cada etapa conta pela data em que ela aconteceu, não pela data de criação da negociação.';
    const profilesById = useMemo(() => new Map(profiles.map(p => [String(p.id), p])), [profiles]);

    /** O que entrou na lista de cada etapa — a mesma regra que somou o número. */
    const explicacaoDaEtapa = (key: string): string => {
        const base = key === 'prospeccao'
            ? 'negociações criadas no período'
            : key === CHAVE_EM_ANALISE
                ? 'termo assinado no período e hoje na fila da validação de dados, esperando a analista (inclui quem voltou para reanálise). Quem está com pendência fica no card Com Pendência'
                : key === CHAVE_COM_PENDENCIA
                    ? 'termo assinado no período que a analista já analisou e devolveu com pendência (documento, garantia ou confirmação). Espera o cliente ou o vendedor e não soma em Em Validação de Dados'
                    : key === 'termo_reprovado'
                    ? 'conta pela data da reprovação, mesmo que a negociação tenha sido recuperada depois'
                    : 'conta pela data em que a etapa aconteceu, não pela criação da negociação';
        return soNoPeriodo && key !== 'prospeccao'
            ? `${base} · só as negociações com todas as etapas anteriores, da criação em diante, dentro do ${recorte}`
            : base;
    };

    /** Abre a lista de uma etapa (card, barra do funil ou célula da tabela por vendedor). */
    const abrirEtapa = (key: string, rows?: readonly EventFunnelRow[], titulo?: string) => {
        const etapa = key === CHAVE_EM_ANALISE
            ? computed.emAnaliseStage
            : key === CHAVE_COM_PENDENCIA
                ? computed.comPendenciaStage
                : key === 'termo_reprovado'
                ? computed.reprovadoStage
                : computed.funnelStages.find(s => s.key === key);
        if (!etapa) return;
        setLista({
            titulo: titulo ?? etapa.label,
            descricao: `${periodoLabel} · ${explicacaoDaEtapa(key)}`,
            // "Em validação" e "Com pendência" mostram a data da assinatura.
            chaveData: key === CHAVE_EM_ANALISE || key === CHAVE_COM_PENDENCIA ? 'termo_assinado' : key,
            rows: [...(rows ?? etapa.rows)],
        });
    };

    /** Abre a lista de um card que não é etapa do funil (criadas no período, status de hoje). */
    const abrirCoorte = (titulo: string, rows: readonly NegRow[], explicacao: string) => setLista({
        titulo,
        descricao: `${periodoLabel} · ${explicacao}`,
        chaveData: 'prospeccao',
        rows: rows as unknown as EventFunnelRow[],
    });

    const abrirCelulaVendedor = (linha: LinhaVendedor, coluna: ColunaVendedor, titulo: string) =>
        abrirEtapa(coluna, linha.celulas[coluna].rows, titulo);

    const linhasLista: LinhaNegociacao[] = useMemo(
        () => (lista ? linhasDaEtapa({ key: lista.chaveData, rows: lista.rows }, profilesById) : []),
        [lista, profilesById],
    );

    const exportarLista = async (visiveis: LinhaNegociacao[]) => {
        if (!lista) return;
        setExportandoLista(true);
        try {
            await baixarPlanilhas(
                [planilhaNegociacoes(visiveis)],
                `negociacoes_${slugArquivo(lista.titulo)}_${slugArquivo(periodoLabel)}${soNoPeriodo ? '_so_no_periodo' : ''}`,
            );
        } catch (e: any) {
            console.error('Erro ao exportar a lista de negociações:', e);
            addToast(e?.message || 'Não foi possível gerar o Excel.', 'error');
        } finally {
            setExportandoLista(false);
        }
    };

    const buildExportInput = (): AnalyticsExportInput => {
        const filtrosParts: string[] = [];
        if (selectedResponsibles.length > 0) filtrosParts.push(`${selectedResponsibles.length} responsável(is)`);
        if (selectedCanais.length > 0) filtrosParts.push(`Canal: ${selectedCanais.join(', ')}`);
        if (selectedCidades.length > 0) filtrosParts.push(`Cidade: ${selectedCidades.join(', ')}`);
        if (selectedOrigens.length > 0) filtrosParts.push(`Origem: ${selectedOrigens.join(', ')}`);
        if (selectedStatuses.length > 0) filtrosParts.push(`Status: ${selectedStatuses.map(s => STATUS_LABELS[s] || s).join(', ')}`);
        // Os números mudam muito entre as duas contagens: o arquivo diz qual é.
        if (soNoPeriodo) filtrosParts.push(`Contagem: só o que aconteceu no ${recorte}`);

        const kpis: Array<[string, string | number]> = [
            ['Total de Leads', fmtNum(computed.total)],
            ['Leads Ativos', fmtNum(computed.ativos)],
            ['Leads Congelados', fmtNum(computed.congelados)],
            ['Leads Perdidos', fmtNum(computed.perdidos)],
            ['Simulações Emitidas', fmtNum(computed.simulacoes)],
            ['Termos Assinados', fmtNum(computed.assinados)],
            ['Em Validação de Dados', fmtNum(computed.emAnaliseStage.leads)],
            ['Com Pendência', fmtNum(computed.comPendenciaStage.leads)],
            ['Termos Aprovados', fmtNum(computed.aprovados)],
            ['Conversão Geral', fmtPct(computed.conversaoGeral)],
            ['kWh Aprovado', fmtKwh(computed.kwhAprovado)],
            ['kWh Assinado', fmtKwh(computed.kwhAssinado)],
            ['Ticket Médio', fmtKwh(computed.ticketMedio)],
        ];

        const funil = funnelChartData.map(d => ({ etapa: d.name, leads: d.valor, kwh: d.kwh }));

        // Mesma base do Painel de Status na tela (snapshot atual, não coorte do período) —
        // senão o Excel/PDF exportado diverge do que a tela mostra.
        const statusPanelTotal = filteredExcetoPeriodo.length;
        const statusPanelData = (['em_analise', 'com_pendencia', 'aprovados', 'reprovados', 'outros'] as StatusBucket[]).map(bucket => {
            const list = filteredExcetoPeriodo.filter(r => statusPanelBucket(r) === bucket);
            const kwh = list.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
            return { status: STATUS_BUCKET_LABELS[bucket], qtd: list.length, kwh, pct: statusPanelTotal > 0 ? (list.length / statusPanelTotal) * 100 : 0 };
        });

        // Leads = coorte (criados no período); aprovados/kWh = por data do evento — mesmo
        // critério do ChannelPerformanceBlock, pra exportação bater com o que está na tela.
        const canalData = (!isParceiroRole && !isVendedor) ? CHANNELS.map(ch => {
            const chLeadsCoorte = filtered.filter(r => r.status !== 'perdido' && canalOf(r.responsavel_id) === ch).length;
            const chTodos = filteredExcetoPeriodo.filter(r => r.status !== 'perdido' && canalOf(r.responsavel_id) === ch);
            // Mesma regra do ChannelPerformanceBlock, inclusive a contagem escolhida.
            const jornadaOk = (r: NegRow, etapa: string) => !soNoPeriodo || jornadaNoPeriodo(r as any, etapa, periodo, customRange);
            const aprovadosRows = chTodos.filter(r => {
                const dataAprovacao = aprovacaoAindaValida(r);
                return !!dataAprovacao && inPeriodo(dataAprovacao, periodo, customRange) && jornadaOk(r, 'termo_aprovado');
            });
            const kwhAprovado = aprovadosRows.reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
            const kwhAssinado = chTodos
                .filter(r => r.data_assinatura_termo && inPeriodo(r.data_assinatura_termo, periodo, customRange) && jornadaOk(r, 'termo_assinado'))
                .reduce((s, r) => s + (r.consumo_medio_kwh || 0), 0);
            return { canal: ch, leads: chLeadsCoorte, aprovados: aprovadosRows.length, kwhAprovado, kwhAssinado, conversao: chLeadsCoorte > 0 ? (aprovadosRows.length / chLeadsCoorte) * 100 : 0 };
        }) : [];

        const rankingData = computed.ranking.map(r => ({
            nome: respNome(r.id), leads: r.leads, aprovados: r.aprovados, assinados: r.assinados,
            kwhAprovado: r.volumeAprovado, kwhAssinado: r.volumeAssinado, conversao: r.conversao,
        }));

        const segmentoData = computed.segData.map(s => ({ segmento: s.segmento, total: s.total, simulacao: s.simulacao, aprovado: s.termo_aprovado, conversao: s.conversao, kwh: s.kwh }));

        let meta: AnalyticsExportInput['meta'] = null;
        if (isGestor && mesUnico) {
            const totalMetaForExport = metaDoRecorte;
            const realizadoForExport = linhasAprovadasNoFechamento(filteredExcetoPeriodo, mesUnico.mes, mesUnico.ano).reduce((a, r) => a + r.kwh, 0);
            meta = {
                mesLabel, meta: totalMetaForExport, realizado: realizadoForExport,
                pct: totalMetaForExport > 0 ? (realizadoForExport / totalMetaForExport) * 100 : 0,
                gap: Math.max(0, totalMetaForExport - realizadoForExport),
            };
        }

        const porVendedor: AnalyticsExportInput['porVendedor'] = (!isParceiroRole && !isVendedor) ? {
            etapas: ['Prospecção', 'Simulação', 'Termo Enviado', 'Termo Assinado', 'Em Validação de Dados', 'Com Pendência', 'Termo Aprovado', 'Termo Reprovado'],
            linhas: [...computed.vendedores, computed.vendedoresTotal].map(l => ({
                nome: l.nome,
                celulas: COLUNAS_VENDEDOR.map(c => ({ qtd: l.celulas[c].qtd, kwh: l.celulas[c].kwh })),
            })),
        } : null;

        return { periodoLabel, filtrosLabel: filtrosParts.join(' · '), kpis, funil, statusPanel: statusPanelData, canal: canalData, ranking: rankingData, motivosPerda: computed.motivosList, segmento: segmentoData, meta, porVendedor };
    };

    const handleExportExcel = () => exportAnalyticsExcel(buildExportInput());
    const handleExportPdf = () => exportAnalyticsPdf(buildExportInput(), computed.insights.map(i => i.text));

    const handleCopyResumo = async () => {
        const pior = computed.leadStepRates.reduce((min, s) => (s.rate < min.rate ? s : min), computed.leadStepRates[0]);
        const top3 = computed.ranking.slice(0, 3).map((r, i) => `${i + 1}º ${respNome(r.id)} (${RANKING_VALUE_FORMAT[rankingSortKey](rankingKeyValue(r, rankingSortKey))})`).join(' · ');
        const texto = [
            `📊 Dashboard Comercial — ${periodoLabel}${soNoPeriodo ? ` (só o que aconteceu no ${recorte})` : ''}`,
            `Leads: ${fmtNum(computed.total)} · Ativos: ${fmtNum(computed.ativos)} · Aprovados: ${fmtNum(computed.aprovados)} (${fmtKwh(computed.kwhAprovado)})`,
            `Conversão geral: ${fmtPct(computed.conversaoGeral)}`,
            pior ? `Gargalo: ${pior.from} → ${pior.to} (${fmtPct(pior.rate)})` : null,
            top3 ? `Top consultores: ${top3}` : null,
        ].filter(Boolean).join('\n');
        await copyToClipboard(texto);
        setResumoCopiado(true);
        setTimeout(() => setResumoCopiado(false), 2000);
    };

    const handleExportImage = async () => {
        setExportingImage(true);
        // A imagem carrega o MESMO payload do Excel e do PDF — uma terceira saída do
        // mesmo dado, não um resumo paralelo. O card só é montado aqui: fora da
        // exportação o dashboard não paga 1080px de DOM oculto nem o recálculo do
        // payload a cada render.
        setResumoVisualData({ ...buildExportInput(), insights: computed.insights.map(i => i.text) });
        try {
            // Dois frames: um para o React montar o card, outro para o browser pintá-lo
            // antes do html2canvas ler o DOM.
            await new Promise<void>(r => requestAnimationFrame(() => r()));
            await new Promise<void>(r => requestAnimationFrame(() => r()));
            if (!resumoVisualRef.current) return;
            const html2canvas = (await import('html2canvas')).default;
            const canvas = await html2canvas(resumoVisualRef.current, { scale: 2, backgroundColor: '#ffffff' });
            const link = document.createElement('a');
            link.download = `Analytics_Comercial_${new Date().toISOString().slice(0, 10)}.png`;
            link.href = canvas.toDataURL('image/png');
            link.click();
        } catch (err) {
            console.error('Erro ao gerar imagem do resumo:', err);
        } finally {
            setResumoVisualData(null);
            setExportingImage(false);
        }
    };

    if (loading) {
        return <div className="flex items-center justify-center h-64"><LoadingSpinner label="Carregando analytics..." size="sm" /></div>;
    }

    return (
        <div className="space-y-4 px-2 sm:px-0">
            {/* Header */}
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-xl">
                        <BarChart3 size={22} />
                    </div>
                    <div>
                        <h1 className="text-lg font-bold text-gray-900 dark:text-white leading-tight">Analytics Comercial</h1>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Inteligência de conversão do pipeline comercial</p>
                    </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-end">
                    {!isVendedor && (
                        <ResponsibleFilter
                            selectedResponsibles={selectedResponsibles}
                            setSelectedResponsibles={setSelectedResponsibles}
                            profiles={profiles}
                            currentUserProfile={profile}
                            viewContext="crm"
                        />
                    )}
                    {!isParceiroRole && !isVendedor && (
                        <MultiSelectDropdown label="Canal" allLabel="Todos os canais" options={[...CHANNELS]} selected={selectedCanais} onChange={setSelectedCanais} />
                    )}
                    {!isParceiroRole && (
                        <>
                            <MultiSelectDropdown label="Região" allLabel="Todas as cidades" options={cidadeOptions} selected={selectedCidades} onChange={setSelectedCidades} />
                            <MultiSelectDropdown label="Origem" allLabel="Todas as origens" options={origemOptions} selected={selectedOrigens} onChange={setSelectedOrigens} />
                            <MultiSelectDropdown
                                label="Status"
                                allLabel="Todos os status"
                                options={statusOptions}
                                selected={selectedStatuses.map(s => STATUS_LABELS[s] || s)}
                                onChange={labels => setSelectedStatuses(labels.map(l => statusLabelToValue[l] || l))}
                            />
                        </>
                    )}
                    <select
                        value={periodo}
                        onChange={e => setPeriodo(e.target.value as Periodo)}
                        className="text-sm font-medium px-3 py-2 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-200 focus:outline-none"
                    >
                        {PERIODOS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                    {periodo === 'personalizado' && (
                        <div className="flex items-center gap-1.5 px-2 py-1 rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700">
                            <input
                                type="date"
                                value={customRange.inicio}
                                onChange={e => setCustomRange(c => ({ ...c, inicio: e.target.value }))}
                                className="text-sm bg-transparent text-gray-700 dark:text-gray-200 outline-none [color-scheme:light] dark:[color-scheme:dark]"
                            />
                            <span className="text-gray-400 text-xs">→</span>
                            <input
                                type="date"
                                value={customRange.fim}
                                onChange={e => setCustomRange(c => ({ ...c, fim: e.target.value }))}
                                className="text-sm bg-transparent text-gray-700 dark:text-gray-200 outline-none [color-scheme:light] dark:[color-scheme:dark]"
                            />
                        </div>
                    )}
                </div>
            </div>

            {/* Exportação executiva (não se aplica ao parceiro) + como o funil conta */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                {!isParceiroRole && (
                    <ExportPanel
                        onExportExcel={handleExportExcel}
                        onExportPdf={handleExportPdf}
                        onCopyResumo={handleCopyResumo}
                        onExportImage={handleExportImage}
                        resumoCopiado={resumoCopiado}
                        exportingImage={exportingImage}
                    />
                )}
                <div className="lg:ml-auto">
                    <SeletorContagemFunil valor={contagem} onChange={setContagem} umMes={!!mesUnico} />
                </div>
            </div>

            {/* Link para captar clientes (#6511) — só parceiro/vendedor_parceiro */}
            {isParceiroRole && <LinkCaptacaoParceiro codigo={profile?.codigo_indicacao} />}

            {/* Link de indicação — só parceiro/vendedor_parceiro */}
            {isParceiroRole && (
                <div className="bg-gradient-to-r from-primary to-primary/80 rounded-xl px-4 py-4 text-white">
                    <div className="flex items-center gap-3 mb-3">
                        <div className="p-2 rounded-lg bg-white/15 shrink-0">
                            <UserPlus size={18} />
                        </div>
                        <div>
                            <p className="text-sm font-bold">Seu link de indicação para novos consultores</p>
                            <p className="text-xs text-white/80">Convide pessoas para vender energia com você e ganhe bônus sobre toda a rede.</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                        <div className="flex-1 min-w-[180px] px-3 py-2 rounded-full bg-white/10 border border-white/20 text-sm font-mono font-bold truncate">
                            {profile?.codigo_indicacao || (gerandoCodigo ? 'Gerando código...' : '—')}
                        </div>
                        <button
                            onClick={handleCopiarLink}
                            disabled={!linkIndicacao}
                            title="Copiar link"
                            className="p-2 rounded-full bg-white/15 hover:bg-white/25 transition-colors disabled:opacity-50"
                        >
                            {linkCopiado ? <Check size={16} /> : <Copy size={16} />}
                        </button>
                        <button
                            onClick={handleCompartilharWhatsapp}
                            disabled={!linkIndicacao}
                            title="Compartilhar no WhatsApp"
                            className="p-2 rounded-full bg-green-500 hover:bg-green-600 transition-colors disabled:opacity-50"
                        >
                            <MessageCircle size={16} />
                        </button>
                    </div>
                </div>
            )}

            {isParceiroRole ? (
                /* Lista simplificada: pares com contagem + kWh de cada etapa */
                <div className="grid grid-cols-2 gap-3">
                    <KpiCardKwh label="Simulações Emitidas" value={fmtNum(computed.simulacoes)} kwh={fmtKwh(computed.kwhPorEtapa['simulacao'] || 0)} icon={<BarChart3 size={18} />} onClick={() => abrirEtapa('simulacao', undefined, 'Simulações Emitidas')} />
                    <KpiCardKwh label="Aguardando Assinatura" value={fmtNum(computed.aguardandoAssinatura)} kwh={fmtKwh(computed.kwhAguardandoAssinatura)} icon={<FileSignature size={18} />} onClick={() => abrirCoorte('Aguardando Assinatura', computed.listas.aguardandoAssinatura, 'criadas no período e com o termo enviado, ainda sem assinatura (status de hoje)')} />
                    <KpiCardKwh label="Termos Assinados" value={fmtNum(computed.assinados)} kwh={fmtKwh(computed.kwhAssinado)} icon={<Award size={18} />} onClick={() => abrirEtapa('termo_assinado', undefined, 'Termos Assinados')} />
                    <KpiCardKwh label="Termos Aprovados" value={fmtNum(computed.aprovados)} kwh={fmtKwh(computed.kwhAprovado)} icon={<Award size={18} />} onClick={() => abrirEtapa('termo_aprovado', undefined, 'Termos Aprovados')} />
                    <KpiCardKwh label="Perdido" value={fmtNum(computed.perdidos)} kwh={fmtKwh(computed.kwhPerdido)} icon={<TrendingDown size={18} />} attention onClick={() => abrirCoorte('Perdido', computed.listas.perdidos, 'criadas no período e perdidas (status de hoje)')} />
                    <KpiCardKwh label="Reprovado" value={fmtNum(computed.reprovados)} kwh={fmtKwh(computed.kwhReprovado)} icon={<XCircle size={18} />} attention onClick={() => abrirCoorte('Reprovado', computed.listas.reprovadosStatus, 'criadas no período e com termo reprovado (status de hoje)')} />
                </div>
            ) : (
                <>
                    {computedAnterior && (
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 px-4 py-2.5 rounded-xl bg-gray-50 dark:bg-gray-800/60 border border-gray-100 dark:border-gray-700">
                            <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wide">vs. período anterior</span>
                            <span className="flex items-center gap-1.5 text-[11px]"><span className="text-gray-500 dark:text-gray-400">Leads</span><TrendChip atual={computed.total} anterior={computedAnterior.total} fmt={fmtNum} /></span>
                            <span className="flex items-center gap-1.5 text-[11px]"><span className="text-gray-500 dark:text-gray-400">Aprovados</span><TrendChip atual={computed.aprovados} anterior={computedAnterior.aprovados} fmt={fmtNum} /></span>
                            <span className="flex items-center gap-1.5 text-[11px]"><span className="text-gray-500 dark:text-gray-400">kWh aprovado</span><TrendChip atual={computed.kwhAprovado} anterior={computedAnterior.kwhAprovado} fmt={fmtKwh} /></span>
                            <span className="flex items-center gap-1.5 text-[11px]"><span className="text-gray-500 dark:text-gray-400">Conversão</span><TrendChip atual={computed.conversaoGeral} anterior={computedAnterior.conversaoGeral} fmt={fmtPct} /></span>
                        </div>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                        <KpiCard label="Total de Leads" value={fmtNum(computed.total)} icon={<Users size={20} />} onClick={() => abrirCoorte('Total de Leads', computed.listas.criadas, 'negociações criadas no período')} />
                        <KpiCard label="Leads Ativos" value={fmtNum(computed.ativos)} icon={<TrendingUp size={20} />} tone="text-green-600 dark:text-green-400" onClick={() => abrirCoorte('Leads Ativos', computed.listas.ativos, `criadas no período, nem perdidas nem congeladas (sem atividade há +${diasCongelamento} dias)`)} />
                        <KpiCard label="Leads Congelados" value={fmtNum(computed.congelados)} icon={<Snowflake size={20} />} tone="text-sky-600 dark:text-sky-400" onClick={() => abrirCoorte('Leads Congelados', computed.listas.congelados, `criadas no período e sem atividade há +${diasCongelamento} dias`)} />
                        <KpiCard label="Leads Perdidos" value={fmtNum(computed.perdidos)} icon={<TrendingDown size={20} />} tone="text-red-500" onClick={() => abrirCoorte('Leads Perdidos', computed.listas.perdidos, 'criadas no período e perdidas (status de hoje)')} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
                        <KpiCard label="Simulações Emitidas" value={fmtNum(computed.simulacoes)} icon={<BarChart3 size={20} />} tone="text-amber-500" onClick={() => abrirEtapa('simulacao', undefined, 'Simulações Emitidas')} />
                        <KpiCard label="Termos Assinados" value={fmtNum(computed.assinados)} icon={<Award size={20} />} tone="text-teal-600 dark:text-teal-400" onClick={() => abrirEtapa('termo_assinado', undefined, 'Termos Assinados')} />
                        <KpiCard label="Em Validação de Dados" value={fmtKwh(computed.emAnaliseStage.kwh)} sublabel={`${fmtNum(computed.emAnaliseStage.leads)} na fila da analista`} icon={<FileSearch size={20} />} tone="text-sky-600 dark:text-sky-400" onClick={() => abrirEtapa(CHAVE_EM_ANALISE)} />
                        <KpiCard label="Com Pendência" value={fmtKwh(computed.comPendenciaStage.kwh)} sublabel={`${fmtNum(computed.comPendenciaStage.leads)} esperando o cliente · fora da fila`} icon={<FileWarning size={20} />} tone="text-amber-600 dark:text-amber-400" onClick={() => abrirEtapa(CHAVE_COM_PENDENCIA)} />
                        <KpiCard label="Termos Aprovados" value={fmtNum(computed.aprovados)} icon={<Award size={20} />} tone="text-green-600 dark:text-green-400" onClick={() => abrirEtapa('termo_aprovado', undefined, 'Termos Aprovados')} />
                        <KpiCard label="Conversão Geral" value={fmtPct(computed.conversaoGeral)} sublabel="termos aprovados ÷ leads" icon={<TrendingUp size={20} />} tone="text-blue-600 dark:text-blue-400" onClick={() => abrirEtapa('termo_aprovado', undefined, 'Conversão Geral — Termos Aprovados')} />
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        <KpiCard label="Potencial no Funil" value={fmtKwh(computed.kwhPotencial)} icon={<Zap size={20} />} tone="text-blue-600 dark:text-blue-400" onClick={() => abrirCoorte('Potencial no Funil', computed.listas.criadas, 'negociações criadas no período')} />
                        <KpiCard label="kWh Termo Assinado" value={fmtKwh(computed.kwhAssinado)} icon={<Zap size={20} />} tone="text-teal-600 dark:text-teal-400" onClick={() => abrirEtapa('termo_assinado', undefined, 'kWh Termo Assinado')} />
                        <KpiCard label="kWh Aprovado" value={fmtKwh(computed.kwhAprovado)} icon={<Zap size={20} />} tone="text-green-600 dark:text-green-400" onClick={() => abrirEtapa('termo_aprovado', undefined, 'kWh Aprovado')} />
                        <KpiCard label="kWh Perdido" value={fmtKwh(computed.kwhPerdido)} icon={<Zap size={20} />} tone="text-red-500" onClick={() => abrirCoorte('kWh Perdido', computed.listas.perdidos, 'criadas no período e perdidas (status de hoje)')} />
                        <KpiCard label="Ticket Médio" value={fmtKwh(computed.ticketMedio)} sublabel="kWh aprovado ÷ termos aprovados" icon={<Zap size={20} />} tone="text-amber-500" onClick={() => abrirEtapa('termo_aprovado', undefined, 'Ticket Médio — Termos Aprovados')} />
                    </div>
                </>
            )}

            {/* Simulador de Comissão — só para parceiro */}
            {isParceiro && comissaoSimulacao && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                    <div className="flex items-center gap-2 mb-1">
                        <Wallet size={16} className="text-primary" />
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white">Simulador de Bonificação — Mês Atual</h2>
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        Regra de {REGRA_BONIFICACAO.vigencia}: % da fatura com desconto ({formatCurrency(REGRA_BONIFICACAO.tarifa)}/kWh), pela faixa da soma do mês
                    </p>

                    {/* Explicação + kWh em negociação no mês */}
                    <div className="rounded-xl border border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/30 p-5 mb-4 text-center">
                        <p className="text-3xl font-bold text-gray-900 dark:text-white">{fmtKwh(comissaoSimulacao.kwhEmNegociacao)}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">em negociação este mês</p>
                        <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold">
                            <Award size={12} />
                            {comissaoSimulacao.faixaPotencial
                                ? `Faixa simulada: F${comissaoSimulacao.faixaPotencial.i + 1} · ${Math.round(comissaoSimulacao.faixaPotencial.pct * 100)}%`
                                : `Sem faixa ainda (mínimo de ${fmtNum(REGRA_BONIFICACAO.minimo)} kWh por consumidor)`}
                        </div>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-3 max-w-md mx-auto">
                            Dentro do mês corrente o sistema soma o kWh em negociação e simula a bonificação com o plano padrão ({REGRA_BONIFICACAO.padrao}% de desconto), na faixa da tabela abaixo. Desconto maior reduz a bonificação.
                        </p>
                    </div>

                    {/* Total Ganho x Potencial de Ganho */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Total Ganho</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-2">Bonificação dos termos aprovados dentro do mês corrente</p>
                            <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                {comissaoSimulacao.carregandoDescontos ? '…' : formatCurrency(comissaoSimulacao.comissaoTotal)}
                            </p>
                            {comissaoSimulacao.faixaDoMes && !comissaoSimulacao.carregandoDescontos && (
                                <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-1.5">
                                    {comissaoSimulacao.faixaDoMes} · Pix I de {formatCurrency(comissaoSimulacao.pixIRegraNova)} no dia 10 do mês que vem; o resto depois da 1ª fatura paga
                                </p>
                            )}
                        </div>
                        <div className="p-4 rounded-xl bg-white dark:bg-gray-800 border border-gray-100 dark:border-gray-700">
                            <p className="text-[11px] font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Potencial de Ganho</p>
                            <p className="text-[11px] text-gray-400 dark:text-gray-500 mb-2">Tudo que está em negociação dentro do mês corrente, com o plano padrão, na faixa da soma</p>
                            <p className="text-2xl font-bold text-primary">{formatCurrency(comissaoSimulacao.potencialGanho)}</p>
                        </div>
                    </div>

                    {/* Tabela de faixas da bonificação */}
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-2 pr-3 font-semibold">Faixa de kWh no mês</th>
                                    <th className="py-2 font-semibold text-right">Bonificação (% da fatura com desconto)</th>
                                </tr>
                            </thead>
                            <tbody>
                                {REGRA_BONIFICACAO.faixas.map((f, i) => {
                                    const ativa = comissaoSimulacao.faixaPotencial?.i === i;
                                    const { de, ate } = limitesFaixa(i);
                                    return (
                                        <tr key={i} className={`border-b border-gray-50 dark:border-gray-700/50 ${ativa ? 'bg-primary/5 dark:bg-primary/10' : ''}`}>
                                            <td className={`py-2 pr-3 ${ativa ? 'font-bold text-primary' : 'text-gray-600 dark:text-gray-300'}`}>
                                                <span className="font-bold mr-1.5">F{i + 1}</span>
                                                {ate === Infinity ? `Acima de ${fmtNum(de - 1)}` : `${fmtNum(de)} – ${fmtNum(ate)}`} kWh
                                            </td>
                                            <td className={`py-2 text-right font-bold ${ativa ? 'text-primary' : 'text-gray-700 dark:text-gray-200'}`}>{Math.round(f[1] * 100)}%</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* Funil por vendedor — gestão; parceiro e vendedor só veem a própria carteira */}
            {!isParceiroRole && !isVendedor && (
                <FunilPorVendedorBlock
                    linhas={computed.vendedores}
                    total={computed.vendedoresTotal}
                    descricao={`${periodoLabel} · ${textoContagem} Em Validação de Dados = termo assinado no período e hoje na fila da analista. Com Pendência = já analisado, esperando o cliente ou o vendedor.`}
                    onAbrir={abrirCelulaVendedor}
                />
            )}

            {/* Funil macro + taxas */}
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Funil Macro — Conversão Ponta a Ponta</h2>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">Visualize a conversão de ponta a ponta e identifique gargalos. {textoContagem}</p>
                <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={funnelChartData} margin={{ top: 20, right: 16, left: 0, bottom: 0 }}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#37415122" />
                            <XAxis dataKey="name" tick={{ fontSize: 11 }} interval={0} />
                            <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                            <Tooltip formatter={(v: any, name: any) => name === 'kwh' ? [fmtKwh(Number(v)), 'Volume'] : [fmtNum(Number(v)), 'Leads']} />
                            <Bar dataKey="valor" radius={[6, 6, 0, 0]} cursor="pointer" onClick={(_: any, index: number) => { const k = funnelChartData[index]?.key; if (k) abrirEtapa(k); }}>
                                <LabelList dataKey="valor" position="top" style={{ fontSize: 12, fontWeight: 700 }} />
                                {funnelChartData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                </div>

                {/* Leads + volume energético por etapa — clique pra ver as negociações da etapa */}
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 mt-3">
                    {funnelChartData.map(d => (
                        <button
                            key={d.name}
                            onClick={() => abrirEtapa(d.key)}
                            className="text-left rounded-xl bg-gray-50 dark:bg-gray-700/40 px-3 py-2 hover:ring-2 hover:ring-primary/40 transition-shadow"
                        >
                            <div className="flex items-center gap-1.5 mb-0.5">
                                <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: d.fill }}></span>
                                <p className="text-[10px] font-bold text-gray-500 dark:text-gray-400 uppercase tracking-wide truncate">{d.name}</p>
                            </div>
                            <p className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{fmtNum(d.valor)} leads</p>
                            <p className="text-[11px] font-semibold text-blue-600 dark:text-blue-400">{fmtKwh(d.kwh)}</p>
                        </button>
                    ))}
                </div>

                {/* Conversão por etapa: leads × kWh (qualidade da carteira) */}
                <div className="flex flex-wrap gap-2 mt-3">
                    {computed.leadStepRates.map((s, i) => {
                        const k = computed.kwhStepRates[i];
                        return (
                            <span key={`${s.from}-${s.to}`} className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                {s.from} → {s.to}:{' '}
                                <span className={s.rate < 30 ? 'text-red-500' : s.rate > 60 ? 'text-green-600 dark:text-green-400' : 'text-amber-500'}>{fmtPct(s.rate)}</span>
                                <span className="text-gray-400"> leads · </span>
                                <span className={(k?.rate ?? 0) < 30 ? 'text-red-500' : (k?.rate ?? 0) > 60 ? 'text-green-600 dark:text-green-400' : 'text-amber-500'}>{fmtPct(k?.rate ?? 0)}</span>
                                <span className="text-gray-400"> kWh</span>
                            </span>
                        );
                    })}
                </div>

            </div>

            {/* Painel de Status — não se aplica ao parceiro. Usa `filteredExcetoPeriodo` (não
                `filtered`) de propósito: é um snapshot do status ATUAL de cada negociação, não
                uma coorte por data de criação — senão uma negociação criada antes do período
                mas aprovada dentro dele (ex.: aprovação com atraso de análise) some do painel
                enquanto ainda conta no Funil Macro/KPIs "Termos Aprovados" (que são por data do
                evento), dando números diferentes pro mesmo "Aprovados" na mesma tela. */}
            {!isParceiroRole && (
                <StatusPanel rows={filteredExcetoPeriodo} total={filteredExcetoPeriodo.length} onOpenDetail={setDetailId} />
            )}

            {/* Performance por Canal — não se aplica ao parceiro nem a um vendedor individual (canal fixo) */}
            {!isParceiroRole && !isVendedor && (
                <ChannelPerformanceBlock rows={filteredExcetoPeriodo} periodo={periodo} customRange={customRange} profiles={profiles} metasMap={metasMap} showMeta={isGestor && !!mesUnico} contagem={contagem} />
            )}

            {/* Meta x Realizado — só gerente/adm, mesmo gate do Performance Comercial */}
            {isGestor && (
                <MetaRealizadoBlock
                    rows={filteredExcetoPeriodo}
                    mesUnico={mesUnico}
                    mesLabel={mesLabel}
                    meta={metaDoRecorte}
                    origemMeta={origemMetaDoRecorte}
                    recorte={recorteFiltrado}
                    metaDoTime={metaDoTime?.valor ?? null}
                    podeEditar={podeEditarMetaDoTime}
                    onSalvarMetaDoTime={salvarMetaDoTime}
                />
            )}

            {/* Evolução — Últimos 6 Meses — só para parceiro/vendedor_parceiro */}
            {isParceiroRole && evolucaoSimulacoes && (
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-1">Evolução de Simulações Propostas — Últimos 6 Meses</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                        Sua evolução comparada com a média de outros {profile?.role === 'parceiro' ? 'parceiros' : 'vendedores parceiros'} da rede
                    </p>
                    <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                            <LineChart data={evolucaoSimulacoes} margin={{ top: 10, right: 16, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#37415122" />
                                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Legend wrapperStyle={{ fontSize: 12 }} />
                                <Line type="monotone" dataKey="voce" name="Você" stroke="#5F6C37" strokeWidth={2.5} dot={{ r: 3 }} />
                                <Line type="monotone" dataKey="media" name={`Média — outros ${profile?.role === 'parceiro' ? 'parceiros' : 'vendedores parceiros'}`} stroke="#94a3b8" strokeWidth={2} strokeDasharray="4 4" dot={{ r: 3 }} />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Motivos de perda + Ranking + Insights — não se aplica ao parceiro */}
            {!isParceiroRole && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                    <h2 className="text-sm font-bold text-red-500 mb-3 flex items-center gap-2"><XCircle size={15} /> Motivos de Perda</h2>
                    {computed.motivosList.length === 0 ? (
                        <p className="text-xs text-gray-400">Nenhuma perda no período.</p>
                    ) : (
                        <div className="space-y-2.5">
                            {computed.motivosList.slice(0, 7).map(([motivo, qtd]) => {
                                const pct = computed.perdidos > 0 ? (qtd / computed.perdidos) * 100 : 0;
                                return (
                                    <div key={motivo}>
                                        <div className="flex justify-between text-xs mb-1">
                                            <span className="text-gray-600 dark:text-gray-300 truncate pr-2">{motivo}</span>
                                            <span className="font-bold text-gray-800 dark:text-gray-100 shrink-0">{qtd} ({pct.toFixed(0)}%)</span>
                                        </div>
                                        <div className="h-1.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                                            <div className="h-full rounded-full bg-red-400" style={{ width: `${pct}%` }}></div>
                                        </div>
                                    </div>
                                );
                            })}
                            <div className="mt-3 p-3 rounded-xl bg-sky-50 dark:bg-sky-900/20">
                                <p className="text-xs font-bold text-sky-700 dark:text-sky-300 flex items-center gap-1.5"><Snowflake size={13} /> Taxa de Congelamento: {fmtPct(computed.taxaCongelamento)}</p>
                                <p className="text-[11px] text-sky-600/80 dark:text-sky-400/80 mt-0.5">{computed.congelados} leads sem atividade há +{diasCongelamento} dias</p>
                            </div>
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                    <div className="flex items-center justify-between gap-2 mb-3">
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white flex items-center gap-2"><Award size={15} className="text-amber-500" /> Ranking de Consultores</h2>
                        <select
                            value={rankingSortKey}
                            onChange={e => setRankingSortKey(e.target.value as RankingSortKey)}
                            className="text-[11px] font-semibold px-2 py-1 rounded-lg bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-300 focus:outline-none"
                        >
                            {RANKING_SORT_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                        </select>
                    </div>
                    {computed.ranking.length === 0 ? (
                        <p className="text-xs text-gray-400">Sem dados no período.</p>
                    ) : (
                        <div className="space-y-2">
                            {computed.ranking.map((r, i) => {
                                const value = rankingKeyValue(r, rankingSortKey);
                                const maxValue = rankingKeyValue(computed.ranking[0], rankingSortKey) || 1;
                                const sortLabel = RANKING_SORT_OPTIONS.find(o => o.value === rankingSortKey)?.label || '';
                                return (
                                    <div key={r.id} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                                        <div className="flex items-center gap-2 mb-1.5">
                                            <span className={`w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center text-white ${i === 0 ? 'bg-amber-400' : i === 1 ? 'bg-gray-400' : i === 2 ? 'bg-orange-400' : 'bg-gray-300 dark:bg-gray-600'}`}>{i + 1}</span>
                                            <p className="text-xs font-bold text-gray-800 dark:text-gray-100 truncate flex-1">{respNome(r.id)}</p>
                                            <span className="text-[11px] font-bold text-blue-600 dark:text-blue-400 shrink-0" title={sortLabel}>{RANKING_VALUE_FORMAT[rankingSortKey](value)}</span>
                                        </div>
                                        <div className="grid grid-cols-5 gap-1.5 text-[11px]">
                                            <div><span className="text-gray-400 block">Leads</span><span className="font-bold text-gray-700 dark:text-gray-200">{r.leads}</span></div>
                                            <div><span className="text-gray-400 block">Aprovados</span><span className="font-bold text-green-600 dark:text-green-400">{r.aprovados}</span></div>
                                            <div><span className="text-gray-400 block">Assinados</span><span className="font-bold text-teal-600 dark:text-teal-400">{r.assinados}</span></div>
                                            <div><span className="text-gray-400 block">kWh assin.</span><span className="font-bold text-teal-600 dark:text-teal-400">{fmtKwh(r.volumeAssinado)}</span></div>
                                            <div><span className="text-gray-400 block">Conversão</span><span className="font-bold text-blue-600 dark:text-blue-400">{fmtPct(r.conversao)}</span></div>
                                        </div>
                                        <div className="mt-1.5 h-1 rounded-full bg-gray-200 dark:bg-gray-600 overflow-hidden" title={`${sortLabel} relativo ao 1º colocado`}>
                                            <div className="h-full bg-amber-400 rounded-full" style={{ width: `${Math.min(100, (value / maxValue) * 100)}%` }}></div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>

                <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-2"><Lightbulb size={15} className="text-yellow-500" /> Insights do Funil</h2>
                    <div className="space-y-2">
                        {computed.insights.map((ins, i) => (
                            <div
                                key={i}
                                className={`p-3 rounded-xl text-xs leading-relaxed ${ins.tone === 'warn'
                                    ? 'bg-amber-50 dark:bg-amber-900/20 text-amber-800 dark:text-amber-300'
                                    : ins.tone === 'good'
                                        ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-300'
                                        : 'bg-blue-50 dark:bg-blue-900/20 text-blue-800 dark:text-blue-300'
                                    }`}
                            >
                                {ins.text}
                            </div>
                        ))}
                        {computed.insights.length === 0 && <p className="text-xs text-gray-400">Sem insights para o período selecionado.</p>}
                    </div>
                </div>
            </div>
            )}

            {/* SLA de Atendimento (Closers e BDRs) — auto-contido, não depende dos filtros de período/segmento acima */}
            {!isParceiroRole && <SlaAtendimentoBlock />}

            {/* Funil por Segmento — não se aplica ao parceiro */}
            {!isParceiroRole && (
            <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-5">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                    <div>
                        <h2 className="text-sm font-bold text-gray-900 dark:text-white">Análise de Funil por Segmento</h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Distribuição de leads por etapa e segmento</p>
                    </div>
                    <div className="flex items-center p-1 bg-gray-100 dark:bg-gray-700/50 rounded-full border border-gray-200 dark:border-gray-700">
                        {SEG_TABS.map(t => (
                            <button
                                key={t}
                                onClick={() => setSegTab(t)}
                                className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-all ${segTab === t ? 'bg-white dark:bg-gray-600 text-primary shadow-sm' : 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'}`}
                            >
                                {t}
                            </button>
                        ))}
                    </div>
                </div>

                {computed.segData.length === 0 ? (
                    <p className="text-xs text-gray-400">Sem dados de segmento no período. Os segmentos são capturados nas novas negociações (via UC).</p>
                ) : segTab === 'Distribuição' ? (
                    <div className="h-80">
                        <ResponsiveContainer width="100%" height="100%">
                            <BarChart data={computed.segData.slice(0, 12)} margin={{ top: 10, right: 16, left: 0, bottom: 30 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#37415122" />
                                <XAxis dataKey="segmento" tick={{ fontSize: 10 }} interval={0} angle={-25} textAnchor="end" />
                                <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
                                <Tooltip />
                                <Bar dataKey="prospeccao" name="Prospecção" fill="#3b82f6" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="simulacao" name="Simulação" fill="#a855f7" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="termo_enviado" name="Termo Enviado" fill="#eab308" radius={[3, 3, 0, 0]} />
                                <Bar dataKey="termo_aprovado" name="Termo Aprovado" fill="#22c55e" radius={[3, 3, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                ) : segTab === 'Conversão' ? (
                    <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                            <thead>
                                <tr className="text-left text-gray-400 border-b border-gray-100 dark:border-gray-700">
                                    <th className="py-2 pr-3 font-semibold">Segmento</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Leads</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Simulações</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Aprovados</th>
                                    <th className="py-2 pr-3 font-semibold text-right">Conversão</th>
                                    <th className="py-2 font-semibold text-right">kWh no Funil</th>
                                </tr>
                            </thead>
                            <tbody>
                                {computed.segData.map(s => (
                                    <tr key={s.segmento} className="border-b border-gray-50 dark:border-gray-700/50">
                                        <td className="py-2 pr-3 font-semibold text-gray-800 dark:text-gray-100">{s.segmento}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{s.total}</td>
                                        <td className="py-2 pr-3 text-right text-gray-600 dark:text-gray-300">{s.simulacao}</td>
                                        <td className="py-2 pr-3 text-right text-green-600 dark:text-green-400 font-semibold">{s.termo_aprovado}</td>
                                        <td className={`py-2 pr-3 text-right font-bold ${s.conversao >= 20 ? 'text-green-600 dark:text-green-400' : s.conversao >= 8 ? 'text-amber-500' : 'text-red-500'}`}>{fmtPct(s.conversao)}</td>
                                        <td className="py-2 text-right text-gray-600 dark:text-gray-300">{fmtKwh(s.kwh)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                        {Object.entries(computed.motivosPorSeg).map(([seg, motivos]) => {
                            const totalSeg = Object.values(motivos).reduce((a, b) => a + b, 0);
                            return (
                                <div key={seg} className="p-3 rounded-xl bg-gray-50 dark:bg-gray-700/40">
                                    <p className="text-xs font-bold text-gray-800 dark:text-gray-100 mb-2">{seg} <span className="text-gray-400 font-normal">({totalSeg} perdas)</span></p>
                                    {Object.entries(motivos).sort((a, b) => b[1] - a[1]).slice(0, 4).map(([m, q]) => (
                                        <div key={m} className="flex justify-between text-[11px] py-0.5">
                                            <span className="text-gray-500 dark:text-gray-400 truncate pr-2">{m}</span>
                                            <span className="font-bold text-gray-700 dark:text-gray-200 shrink-0">{q}</span>
                                        </div>
                                    ))}
                                </div>
                            );
                        })}
                        {Object.keys(computed.motivosPorSeg).length === 0 && <p className="text-xs text-gray-400">Nenhuma perda com segmento no período.</p>}
                    </div>
                )}
            </div>
            )}

            <ListaNegociacoesModal
                aberto={!!lista}
                etapa={lista?.titulo ?? ''}
                descricao={lista?.descricao ?? ''}
                linhas={linhasLista}
                exportando={exportandoLista}
                onFechar={() => setLista(null)}
                onExportar={exportarLista}
            />

            <NegotiationDetailModal
                negotiationId={detailId}
                isOpen={detailId !== null}
                onClose={() => setDetailId(null)}
                onUpdate={() => setRefetchTick(t => t + 1)}
            />

            {/* Fora da viewport — só existe pra ser capturado por html2canvas no export de imagem */}
            {resumoVisualData && <ResumoVisualCard ref={resumoVisualRef} data={resumoVisualData} />}
        </div>
    );
};

export default AnalyticsComercialView;
